import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHead } from "@/pages/admin/AdminLayout";
import { api, gbp, shortDate } from "@/lib/api";

const Kpi = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-card border border-border p-5" data-testid={testid}>
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="font-display text-2xl font-extrabold mt-1">{value}</p>
  </div>
);

const Breakdown = ({ title, rows, testid }) => (
  <div className="rounded-2xl bg-card border border-border p-5" data-testid={testid}>
    <p className="font-display font-bold mb-3">{title}</p>
    {rows.length === 0 && <p className="text-sm text-muted-foreground">No paid bookings in this period.</p>}
    {rows.map((r) => (
      <div key={r.name} className="flex justify-between py-2 border-b border-border last:border-0 text-sm">
        <span>{r.name}</span><span className="text-muted-foreground">{r.bookings} · <span className="text-foreground font-semibold">{gbp(r.revenue)}</span></span>
      </div>
    ))}
  </div>
);

export default function Reports() {
  const [days, setDays] = useState("30");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/reports", { params: { days } }).then((r) => setD(r.data)); }, [days]);
  return (
    <div className="fade-up">
      <PageHead eyebrow="Insights" title="Reports">
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40 bg-card" data-testid="reports-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </PageHead>
      {d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Revenue" value={gbp(d.total_revenue)} testid="report-total-revenue" />
            <Kpi label="Paid bookings" value={d.total_bookings} testid="report-total-bookings" />
            <Kpi label="Cancelled" value={d.cancelled} testid="report-cancelled" />
            <Kpi label="No-shows" value={d.no_shows} testid="report-no-shows" />
          </div>
          <div className="rounded-2xl bg-card border border-border p-5 mt-6" data-testid="report-revenue-chart">
            <p className="font-display font-bold mb-4">Revenue by booking date</p>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={d.series}>
                <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2E5B4B" stopOpacity={0.35} /><stop offset="100%" stopColor="#2E5B4B" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E2D8" />
                <XAxis dataKey="date" tickFormatter={shortDate} fontSize={11} minTickGap={24} />
                <YAxis fontSize={11} tickFormatter={(v) => `£${v}`} />
                <Tooltip formatter={(v) => gbp(v)} labelFormatter={shortDate} />
                <Area type="monotone" dataKey="revenue" stroke="#2E5B4B" strokeWidth={2} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-2xl bg-card border border-border p-5 mt-6" data-testid="report-bookings-chart">
            <p className="font-display font-bold mb-4">Bookings per day</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={d.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E2D8" />
                <XAxis dataKey="date" tickFormatter={shortDate} fontSize={11} minTickGap={24} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip labelFormatter={shortDate} />
                <Bar dataKey="bookings" fill="#D96B43" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <Breakdown title="By treatment" rows={d.by_service} testid="report-by-service" />
            <Breakdown title="By practitioner" rows={d.by_practitioner} testid="report-by-practitioner" />
          </div>
        </>
      )}
    </div>
  );
}
